import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getGamePokemonName, getGameMoveName, translateMoveName } from '../utils/gameI18n';

// Translation files
const translations = {
  th: {
    // Navigation
    'nav.home': 'หน้าแรก',
    'nav.calendar': 'ปฏิทิน',
    'nav.gbl': 'Go Battle League',
    'nav.admin': 'Admin',
    'nav.logout': 'ออกจากระบบ',
    'nav.login': 'เข้าสู่ระบบ',
    'nav.language': 'ภาษา',
    
    // Home
    'home.title': 'Pokedex',
    'home.subtitle': 'ค้นหาและสำรวจโปเกมอนได้ทั้งภาษาไทยและอังกฤษ',
    'home.search.placeholder': 'ค้นหาโปเกมอน (ชื่อ / อังกฤษ / เลขไอดี)',
    'home.featured': 'โปเกมอนแนะนำ',
    'home.loadMore': 'โหลดเพิ่มเติม',
    'home.noResults': 'ไม่พบข้อมูลโปเกมอน',
    'home.noResultsHint': 'ลองเปลี่ยนคำค้นหาหรือตัวกรองดูสิ',
    'home.refresh': 'รีเฟรช',
    'home.errorTitle': 'เกิดข้อผิดพลาด',
    'home.errorMessage': 'ระบบไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
    'home.retry': 'ลองใหม่',
    'home.refreshPage': 'รีเฟรชหน้า',
    'home.sortName': 'ชื่อ',
    
    // Pokemon Detail
    'detail.title': 'รายละเอียดโปเกมอน',
    'detail.back': 'กลับ',
    'detail.previous': 'ก่อนหน้า',
    'detail.next': 'ถัดไป',
    'detail.combatPower': 'พลังต่อสู้',
    'detail.maxCp': 'Max CP (Level 50)',
    'detail.baseStats': 'สถิติพื้นฐาน',
    'detail.hp': 'HP',
    'detail.attack': 'โจมตี',
    'detail.defense': 'ป้องกัน',
    'detail.specialMove': 'ท่าพิเศษ',
    'detail.goHome': 'กลับไปหน้าหลัก',
    
    // Badges
    'badge.shiny': 'Shiny',
    'badge.shinyTitle': 'มีร่าง Shiny',
    'badge.dynamax': 'Dynamax',
    'badge.dynamaxTitle': 'สามารถ Dynamax ได้',
    'badge.gigantamax': 'Gigantamax',
    'badge.gigantamaxTitle': 'สามารถ Gigantamax ได้',
    'badge.mega': 'Mega',
    'badge.megaTitle': 'มีร่าง Mega',
    'badge.legendary': 'Legendary',
    'badge.mythical': 'Mythical',
    
    // Counters
    'counters.title': 'โปเกมอนที่ต่อกรกับ',
    'counters.subtitle': 'แนะนำโปเกมอนสำหรับต่อสู้',
    'counters.maxBattle': 'Max Battle',
    'counters.defenders': 'Defenders',
    'counters.raid': 'Raid',
    'counters.tip': '💡 โปเกมอนอันดับ 1-3 เป็นตัวเลือกที่ดีที่สุด',
    'counters.fallbackWarning': '⚠️ ใช้ข้อมูลของ Pokemon พื้นฐาน (ไม่มีข้อมูลเฉพาะสำหรับรูปแบบนี้)',
    
    // Moves
    'moves.title': 'ท่าต่อสู้',
    'moves.subtitle': 'Movesets',
    'moves.best': 'ท่าที่ดีที่สุด',
    'moves.fast': 'ท่าเร็ว',
    'moves.fastFull': 'ท่าเร็ว (Fast Moves)',
    'moves.charged': 'ท่าชาร์จ',
    'moves.chargedFull': 'ท่าชาร์จ (Charged Moves)',
    'moves.other': 'ท่าอื่นๆ',
    'moves.combinations': 'ชุดท่า',
    'moves.tip': '💡 DPS = Damage Per Second | TDO = Total Damage Output',
    'moves.tierRanking': 'Tier Ranking',
    'moves.raidAttacker': 'Raid Attacker',
    'moves.gymDefender': 'Gym Defender',
    'moves.tierExplain': '🏆 Tier S/A+ = แข็งแกร่งมาก, A/B = ดี, C/D = ปานกลาง, F = อ่อน',
    'moves.damage': 'ความเสียหาย',
    'moves.energy': 'พลังงาน',
    'moves.duration': 'ระยะเวลา',
    'moves.turns': 'เทิร์น',
    
    // Types
    'type.normal': 'Normal',
    'type.fire': 'Fire',
    'type.water': 'Water',
    'type.electric': 'Electric',
    'type.grass': 'Grass',
    'type.ice': 'Ice',
    'type.fighting': 'Fighting',
    'type.poison': 'Poison',
    'type.ground': 'Ground',
    'type.flying': 'Flying',
    'type.psychic': 'Psychic',
    'type.bug': 'Bug',
    'type.rock': 'Rock',
    'type.ghost': 'Ghost',
    'type.dragon': 'Dragon',
    'type.dark': 'Dark',
    'type.steel': 'Steel',
    'type.fairy': 'Fairy',
    
    // GBL
    'gbl.title': 'Go Battle League',
    'gbl.loading': 'กำลังโหลดข้อมูล Go Battle League...',
    'gbl.searchPlaceholder': 'ค้นหาโปเกมอน...',
    'gbl.allTypes': 'ทุกประเภท',
    'gbl.sortByScore': 'เรียงตามคะแนน',
    'gbl.sortByRank': 'เรียงตามอันดับ',
    'gbl.pokemonRankings': 'อันดับโปเกมอน',
    'gbl.recommendedTeams': 'ทีมแนะนำ',
    'gbl.rank': 'อันดับ',
    'gbl.score': 'คะแนน',
    'gbl.noRankings': 'ยังไม่มีข้อมูลอันดับสำหรับลีกนี้',
    'gbl.noRankingsHint': 'อันดับโปเกมอนจะแสดงที่นี่เมื่อมีข้อมูล',
    'gbl.showing': 'แสดง',
    'gbl.to': 'ถึง',
    'gbl.of': 'จาก',
    'gbl.results': 'รายการ',
    'gbl.loadingMore': 'กำลังโหลดข้อมูลเพิ่มเติม...',
    'gbl.showingAll': 'แสดงผลทั้งหมดแล้ว',
    'gbl.loadingTeams': 'กำลังโหลดทีม...',
    'gbl.all': 'ทั้งหมด',
    'gbl.popularTeams': 'ทีมยอดนิยม',
    'gbl.topTeams': 'Top Teams',
    'gbl.noTeams': 'ยังไม่มีทีมแนะนำสำหรับลีกนี้',
    'gbl.noTeamsHint': 'ทีมแนะนำจะแสดงที่นี่เมื่อมีข้อมูล',
    'gbl.pokemon': 'โปเกมอน',
    
    // Events / Calendar
    'events.title': 'ตารางกิจกรรม',
    'events.important': 'กิจกรรมสำคัญ (Events)',
    'events.today': 'วันนี้',
    'events.loading': 'กำลังโหลดข้อมูล...',
    'events.noEvents': 'ไม่มีกิจกรรมใหม่เริ่มต้นในช่วงนี้',
    'events.byCategory': 'แยกหมวดหมู่',
    'events.byWeek': 'รายสัปดาห์',
    'events.week': 'สัปดาห์ที่',
    'events.description': 'รายละเอียด',
    'events.ended': 'จบแล้ว',
    'events.tba': 'ไม่ระบุ',
    
    // Admin
    'admin.mapping.title': 'การ Mapping ชื่อโปเกมอน',
    'admin.mapping.autoMapping': 'Auto Mapping',
    'admin.mapping.autoMappingProgress': 'กำลังทำ Auto Mapping...',
    'admin.mapping.add': 'เพิ่ม Mapping',
    'admin.mapping.search': 'ค้นหา mapping...',
    'admin.mapping.pvpokeName': 'ชื่อ PvPoke',
    'admin.mapping.pokemon': 'โปเกมอน',
    'admin.mapping.confidence': 'ความแม่นยำ',
    'admin.mapping.matchType': 'การจับคู่',
    'admin.mapping.actions': 'การดำเนินการ',
    'admin.mapping.exactMatch': 'ตรงกัน',
    'admin.mapping.approximate': 'ประมาณ',
    'admin.mapping.edit': 'แก้ไข Mapping',
    'admin.mapping.addNew': 'เพิ่ม Mapping ใหม่',
    'admin.mapping.selectPokemon': 'เลือกโปเกมอน',
    'admin.mapping.confidenceHint': '0.0 = ไม่ตรงกัน, 1.0 = ตรงกันสมบูรณ์',
    'admin.mapping.confirmDelete': 'คุณแน่ใจหรือไม่ที่จะลบ mapping นี้?',
    'admin.mapping.confirmAutoMap': 'คุณแน่ใจหรือไม่ที่จะทำการ auto mapping?',
    'admin.cancel': 'ยกเลิก',
    'admin.update': 'อัพเดต',
    'admin.create': 'สร้าง',
    'admin.league.title': 'จัดการลีก',
    'admin.league.addNew': 'เพิ่มลีกใหม่',
    'admin.league.confirmDelete': 'คุณแน่ใจหรือไม่ที่จะลบลีกนี้?',
    'admin.league.edit': 'แก้ไขลีก',
    'admin.league.create': 'สร้างลีกใหม่',
    'admin.league.nameEn': 'ชื่อลีก (อังกฤษ)',
    'admin.league.nameTh': 'ชื่อลีก (ไทย)',
    'admin.league.cpLimitHint': 'ใส่ 0 สำหรับไม่มีขีดจำกัด',
    'admin.league.sortOrder': 'ลำดับการแสดง',
    'admin.league.descEn': 'คำอธิบาย (อังกฤษ)',
    'admin.league.descTh': 'คำอธิบาย (ไทย)',
    
    // Counters
    'counters.raid': 'Raid',
    'counters.maxBattle': 'Max Battle',
    'counters.defenders': 'Defenders',
    'counters.minTrainers': 'ขั้นต่ำ',
    'counters.recommended': 'แนะนำ',
    'counters.easyWith': 'ง่ายด้วย',

    // Events extra
    'events.raidBattles': 'Raid Battles',
    'events.spotlightHours': 'Spotlight Hours',
    'events.spotlightSubtitle': 'ทุกวันอังคาร 18:00 - 19:00 น.',
    'events.maxMondays': 'Max Mondays',
    'events.maxMondaysSubtitle': 'ทุกวันจันทร์ 18:00 - 19:00 น.',
    'events.calendarTitle': 'ตารางกิจกรรม',
    'events.notFound': 'ไม่พบข้อมูลกิจกรรม',
    'events.loadError': 'เกิดข้อผิดพลาดในการโหลดข้อมูล',
    'events.backToCalendar': 'กลับไปหน้าตารางกิจกรรม',
    'events.back': 'กลับ',

    // Pokemon Detail extra
    'detail.gmaxMove': 'Gigantamax Move',
    'detail.notFound': 'ไม่พบข้อมูลโปเกมอน',
    'detail.loadError': 'เกิดข้อผิดพลาดในการโหลดข้อมูล',

    // Image Gallery
    'gallery.normal': 'ปกติ',
    'gallery.shiny': 'Shiny',

    // Stats
    'stats.maxCp': 'Max CP',
    'stats.total': 'รวม',
    'stats.hp': 'HP',
    'stats.atk': 'ATK',
    'stats.def': 'DEF',
    'stats.attack': 'โจมตี',
    'stats.defense': 'ป้องกัน',

    // Common
    'common.loading': 'กำลังโหลด...',
    'common.error': 'เกิดข้อผิดพลาด',
    'common.noData': 'ไม่พบข้อมูล',
    'common.refreshPage': 'รีเฟรชหน้า',
    'common.dmg': 'DMG',
    'common.dps': 'DPS',
    'common.tdo': 'TDO',
    'common.dpt': 'DPT',
    'common.ept': 'EPT',
    'common.dpe': 'DPE',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.calendar': 'Calendar',
    'nav.gbl': 'Go Battle League',
    'nav.admin': 'Admin',
    'nav.logout': 'Logout',
    'nav.login': 'Login',
    'nav.language': 'Language',
    
    // Home
    'home.title': 'Pokedex',
    'home.subtitle': 'Search and explore Pokemon in Thai and English',
    'home.search.placeholder': 'Search Pokemon (Name / Thai / ID)',
    'home.featured': 'Featured Pokemon',
    'home.loadMore': 'Load More',
    'home.noResults': 'No Pokemon found',
    'home.noResultsHint': 'Try a different search term or filter',
    'home.refresh': 'Refresh',
    'home.errorTitle': 'Error',
    'home.errorMessage': 'Unable to load data. Please try again.',
    'home.retry': 'Retry',
    'home.refreshPage': 'Refresh Page',
    'home.sortName': 'Name',
    
    // Pokemon Detail
    'detail.title': 'Pokemon Details',
    'detail.back': 'Back',
    'detail.previous': 'Previous',
    'detail.next': 'Next',
    'detail.combatPower': 'Combat Power',
    'detail.maxCp': 'Max CP (Level 50)',
    'detail.baseStats': 'Base Stats',
    'detail.hp': 'HP',
    'detail.attack': 'Attack',
    'detail.defense': 'Defense',
    'detail.specialMove': 'Special Move',
    'detail.goHome': 'Go Home',
    
    // Badges
    'badge.shiny': 'Shiny',
    'badge.shinyTitle': 'Has Shiny Form',
    'badge.dynamax': 'Dynamax',
    'badge.dynamaxTitle': 'Can Dynamax',
    'badge.gigantamax': 'Gigantamax',
    'badge.gigantamaxTitle': 'Can Gigantamax',
    'badge.mega': 'Mega',
    'badge.megaTitle': 'Has Mega Evolution',
    'badge.legendary': 'Legendary',
    'badge.mythical': 'Mythical',
    
    // Counters
    'counters.title': 'Counters for',
    'counters.subtitle': 'Recommended Pokemon for battle',
    'counters.maxBattle': 'Max Battle',
    'counters.defenders': 'Defenders',
    'counters.raid': 'Raid',
    'counters.tip': '💡 Pokemon ranked 1-3 are the best choices',
    'counters.fallbackWarning': '⚠️ Using base Pokemon data (no specific data for this form)',
    
    // Moves
    'moves.title': 'Movesets',
    'moves.subtitle': 'Battle Moves',
    'moves.best': 'Best Moveset',
    'moves.fast': 'Fast Move',
    'moves.fastFull': 'Fast Moves',
    'moves.charged': 'Charged Moves',
    'moves.chargedFull': 'Charged Moves',
    'moves.other': 'Other Movesets',
    'moves.combinations': 'Combinations',
    'moves.tip': '💡 DPS = Damage Per Second | TDO = Total Damage Output',
    'moves.tierRanking': 'Tier Rankings',
    'moves.raidAttacker': 'Raid Attacker',
    'moves.gymDefender': 'Gym Defender',
    'moves.tierExplain': '🏆 Tier S/A+ = Very Strong, A/B = Good, C/D = Average, F = Weak',
    'moves.damage': 'Damage',
    'moves.energy': 'Energy',
    'moves.duration': 'Duration',
    'moves.turns': 'turns',
    
    // Types
    'type.normal': 'Normal',
    'type.fire': 'Fire',
    'type.water': 'Water',
    'type.electric': 'Electric',
    'type.grass': 'Grass',
    'type.ice': 'Ice',
    'type.fighting': 'Fighting',
    'type.poison': 'Poison',
    'type.ground': 'Ground',
    'type.flying': 'Flying',
    'type.psychic': 'Psychic',
    'type.bug': 'Bug',
    'type.rock': 'Rock',
    'type.ghost': 'Ghost',
    'type.dragon': 'Dragon',
    'type.dark': 'Dark',
    'type.steel': 'Steel',
    'type.fairy': 'Fairy',
    
    // GBL
    'gbl.title': 'Go Battle League',
    'gbl.loading': 'Loading Go Battle League data...',
    'gbl.searchPlaceholder': 'Search Pokemon...',
    'gbl.allTypes': 'All Types',
    'gbl.sortByScore': 'Sort by Score',
    'gbl.sortByRank': 'Sort by Rank',
    'gbl.pokemonRankings': 'Pokemon Rankings',
    'gbl.recommendedTeams': 'Recommended Teams',
    'gbl.rank': 'Rank',
    'gbl.score': 'Score',
    'gbl.noRankings': 'No rankings data for this league',
    'gbl.noRankingsHint': 'Pokemon rankings will appear here when available',
    'gbl.showing': 'Showing',
    'gbl.to': 'to',
    'gbl.of': 'of',
    'gbl.results': 'results',
    'gbl.loadingMore': 'Loading more data...',
    'gbl.showingAll': 'Showing all results',
    'gbl.loadingTeams': 'Loading teams...',
    'gbl.all': 'All',
    'gbl.popularTeams': 'Popular Teams',
    'gbl.topTeams': 'Top Teams',
    'gbl.noTeams': 'No recommended teams for this league',
    'gbl.noTeamsHint': 'Recommended teams will appear here when available',
    'gbl.pokemon': 'Pokemon',
    
    // Events / Calendar
    'events.title': 'Event Calendar',
    'events.important': 'Important Events',
    'events.today': 'Today',
    'events.loading': 'Loading data...',
    'events.noEvents': 'No new events starting in this period',
    'events.byCategory': 'By Category',
    'events.byWeek': 'Weekly',
    'events.week': 'Week',
    'events.description': 'Description',
    'events.ended': 'Ended',
    'events.tba': 'TBA',
    
    // Admin
    'admin.mapping.title': 'Pokemon Name Mapping',
    'admin.mapping.autoMapping': 'Auto Mapping',
    'admin.mapping.autoMappingProgress': 'Auto Mapping...',
    'admin.mapping.add': 'Add Mapping',
    'admin.mapping.search': 'Search mappings...',
    'admin.mapping.pvpokeName': 'PvPoke Name',
    'admin.mapping.pokemon': 'Pokemon',
    'admin.mapping.confidence': 'Confidence',
    'admin.mapping.matchType': 'Match Type',
    'admin.mapping.actions': 'Actions',
    'admin.mapping.exactMatch': 'Exact Match',
    'admin.mapping.approximate': 'Approximate',
    'admin.mapping.edit': 'Edit Mapping',
    'admin.mapping.addNew': 'Add New Mapping',
    'admin.mapping.selectPokemon': 'Select Pokemon',
    'admin.mapping.confidenceHint': '0.0 = No match, 1.0 = Perfect match',
    'admin.mapping.confirmDelete': 'Are you sure you want to delete this mapping?',
    'admin.mapping.confirmAutoMap': 'Are you sure you want to perform auto mapping?',
    'admin.cancel': 'Cancel',
    'admin.update': 'Update',
    'admin.create': 'Create',
    'admin.league.title': 'League Management',
    'admin.league.addNew': 'Add New League',
    'admin.league.confirmDelete': 'Are you sure you want to delete this league?',
    'admin.league.edit': 'Edit League',
    'admin.league.create': 'Create New League',
    'admin.league.nameEn': 'League Name (English)',
    'admin.league.nameTh': 'League Name (Thai)',
    'admin.league.cpLimitHint': 'Enter 0 for no limit',
    'admin.league.sortOrder': 'Sort Order',
    'admin.league.descEn': 'Description (English)',
    'admin.league.descTh': 'Description (Thai)',
    
    // Counters
    'counters.raid': 'Raid',
    'counters.maxBattle': 'Max Battle',
    'counters.defenders': 'Defenders',
    'counters.minTrainers': 'Minimum',
    'counters.recommended': 'Recommended',
    'counters.easyWith': 'Easy with',

    // Events extra
    'events.raidBattles': 'Raid Battles',
    'events.spotlightHours': 'Spotlight Hours',
    'events.spotlightSubtitle': 'Every Tuesday 6:00 - 7:00 PM',
    'events.maxMondays': 'Max Mondays',
    'events.maxMondaysSubtitle': 'Every Monday 6:00 - 7:00 PM',
    'events.calendarTitle': 'Event Calendar',
    'events.notFound': 'Event not found',
    'events.loadError': 'Failed to load data',
    'events.backToCalendar': 'Back to Calendar',
    'events.back': 'Back',

    // Pokemon Detail extra
    'detail.gmaxMove': 'Gigantamax Move',
    'detail.notFound': 'Pokemon not found',
    'detail.loadError': 'Failed to load data',

    // Image Gallery
    'gallery.normal': 'Normal',
    'gallery.shiny': 'Shiny',

    // Stats
    'stats.maxCp': 'Max CP',
    'stats.total': 'Total',
    'stats.hp': 'HP',
    'stats.atk': 'ATK',
    'stats.def': 'DEF',
    'stats.attack': 'Attack',
    'stats.defense': 'Defense',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error occurred',
    'common.noData': 'No data found',
    'common.refreshPage': 'Refresh Page',
    'common.dmg': 'DMG',
    'common.dps': 'DPS',
    'common.tdo': 'TDO',
    'common.dpt': 'DPT',
    'common.ept': 'EPT',
    'common.dpe': 'DPE',
  }
};

// Detect browser language
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  if (browserLang.startsWith('th')) {
    return 'th';
  }
  return 'en';
};

// Get saved language or detect from browser
const getInitialLanguage = () => {
  const savedLang = localStorage.getItem('pogodex_language');
  if (savedLang && (savedLang === 'th' || savedLang === 'en')) {
    return savedLang;
  }
  return detectBrowserLanguage();
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  // Set language and save to localStorage
  const setLanguage = useCallback((lang) => {
    if (lang === 'th' || lang === 'en') {
      setLanguageState(lang);
      localStorage.setItem('pogodex_language', lang);
    }
  }, []);

  // Toggle between Thai and English
  const toggleLanguage = useCallback(() => {
    const newLang = language === 'th' ? 'en' : 'th';
    setLanguage(newLang);
  }, [language, setLanguage]);

  // Translation function with th → en fallback
  const t = useCallback((key, params = {}) => {
    const translation = translations[language]?.[key] || translations['en']?.[key] || key;
    
    // Dev warning for missing translation keys
    if (process.env.NODE_ENV === 'development' && translation === key && !key.startsWith('type.')) {
      console.warn(`[i18n] Missing translation key: "${key}" for language: "${language}"`);
    }
    
    // Replace params like {name} with actual values
    if (Object.keys(params).length > 0) {
      return Object.entries(params).reduce((str, [param, value]) => {
        return str.replace(new RegExp(`\\{${param}\\}`, 'g'), value);
      }, translation);
    }
    
    return translation;
  }, [language]);

  // Game i18n helpers - ชื่อโปเกมอนและท่าต่อสู้จากข้อมูลเกม
  const gamePokemonName = useCallback((pokemonId) => {
    return getGamePokemonName(pokemonId, language) || null;
  }, [language]);

  const gameMoveName = useCallback((moveId) => {
    return getGameMoveName(moveId, language) || null;
  }, [language]);

  // Translate move name from English to current language
  const tMoveName = useCallback((englishMoveName) => {
    return translateMoveName(englishMoveName, language);
  }, [language]);

  // Effect to update document language attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    gamePokemonName,
    gameMoveName,
    tMoveName,
    isThaiLanguage: language === 'th',
    isEnglishLanguage: language === 'en',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;


